import type {LastQuoteCacheService} from '../last-quote-cache.service';
import type {MessageBusService} from '../message-bus.service';
import type {QuoteDto} from '../shell-communication/dtos/quote-dto.class';
import {Subscription} from 'rxjs';

export interface MarketGuidanceLeg { code: string; kind: number; marketPx?: number; }

/** One dialog's shared-cache ownership. Quotes are guidance and never touch actual fills. */
export class OrderPreviewMarket {
    private tickers: string[] = [];
    private subscription: Subscription;
    private legs: MarketGuidanceLeg[] = [];
    constructor(private cache: LastQuoteCacheService, private bus: MessageBusService, private changed: () => void) {}

    start(legs: MarketGuidanceLeg[]): void {
        this.stop();
        this.legs = legs;
        this.legs.forEach(l => l.marketPx = null);
        this.tickers = legs.map(l => l.code).filter((t, i, all) => !!t && all.indexOf(t) === i);
        // Listen before subscribing/seeding, so a live arrival cannot fall into a bootstrap gap.
        this.subscription = this.bus.of<QuoteDto[]>('QuoteDto').subscribe(m => {
            let touched = false;
            (m.payload || []).forEach(q => { touched = this.apply(q) || touched; });
            if (touched) { this.changed(); }
        });
        this.tickers.forEach(t => {
            const seed = this.cache.subscribeTicker(t);
            if (seed) { this.apply(seed); }
        });
        this.changed();
    }

    private apply(q: QuoteDto): boolean {
        if (!q || !q.ticker) { return false; }
        let touched = false;
        this.legs.forEach(l => {
            if (l.code !== q.ticker) { return; }
            const value = l.kind === 1 ? q.mid : q.lastPx;
            const price = typeof value === 'number' && isFinite(value) ? value : null;
            if (l.marketPx !== price) { l.marketPx = price; touched = true; }
        });
        return touched;
    }

    stop(): void {
        if (this.subscription) { this.subscription.unsubscribe(); this.subscription = null; }
        this.tickers.forEach(t => this.cache.unsubscribeTicker(t));
        this.tickers = [];
        this.legs = [];
    }
}
